import { useMemo } from 'react'
import {
  useReactTable,
  getCoreRowModel,
  type ColumnDef,
  type VisibilityState,
  type OnChangeFn,
} from '@tanstack/react-table'
import Badge from '@atoms/Badge'
import Icon from '@components/utils/Icon'
import Link from '@atoms/Link'
import { Tooltip, TooltipContent, TooltipTrigger } from '@atoms/Tooltip'
import { scopedTranslation } from '@frontend/utils/I18n'
import { formatDate } from '@utils/formatDate'
import type { GroupMembersSearchQueryType } from '@frontend/graphql/queries/groups'
import { supporterProfileUrl } from '../../utils/routes'
import { SupporterTableActions } from './SupporterTableActions'

const t = scopedTranslation('group_show.table')

export type SupporterEdge = GroupMembersSearchQueryType['group']['supporters']['edges'][0]

type UseSupporterTableProps = {
  data: SupporterEdge[]
  groupId: string
  columnVisibility: VisibilityState
  onColumnVisibilityChange: OnChangeFn<VisibilityState>
  canSendIndividualSms: boolean
  canMakePhoneCalls: boolean
  demoSite: boolean
}

export function useSupporterTable({
  data,
  groupId,
  columnVisibility,
  onColumnVisibilityChange,
  canSendIndividualSms,
  canMakePhoneCalls,
  demoSite,
}: UseSupporterTableProps) {
  const columns = useMemo<Array<ColumnDef<SupporterEdge>>>(
    () => [
      {
        id: 'name',
        header: t('name'),
        enableHiding: false,
        size: 200,
        minSize: 160,
        meta: { allowWrap: true },
        cell: ({ row }) => {
          const { id, firstName, lastName } = row.original.node
          const profileUrl = supporterProfileUrl(groupId, id)
          return (
            <Link href={profileUrl} as="modalLink" size="sm" rank="link" color="primary">
              {firstName} {lastName}
            </Link>
          )
        },
      },
      {
        id: 'email',
        header: t('email_address'),
        meta: { hideOnMobile: true },
        accessorFn: (row) => row.node.email ?? null,
      },
      {
        id: 'mobile',
        header: t('mobile'),
        meta: { hideOnMobile: true },
        accessorFn: (row) => row.node.primaryMobile ?? null,
      },
      {
        id: 'city',
        header: t('city'),
        meta: { hideOnMobile: true },
        accessorFn: (row) => row.node.city ?? null,
      },
      {
        id: 'state',
        header: t('state'),
        meta: { hideOnMobile: true },
        accessorFn: (row) => row.node.state ?? null,
      },
      {
        id: 'postcode',
        header: t('postcode'),
        meta: { hideOnMobile: true },
        accessorFn: (row) => row.node.zip ?? null,
      },
      {
        id: 'location',
        header: t('location'),
        meta: { hideOnMobile: true },
        accessorFn: (row) => row.node.location ?? null,
      },
      {
        id: 'tagFilters',
        header: t('tag_filters'),
        meta: { hideOnMobile: true },
        cell: ({ row }) => {
          const tagFilters = row.original.tagFilters ?? []
          if (tagFilters.length === 0) return null
          return (
            <div className="tw:flex tw:flex-wrap tw:gap-1">
              {tagFilters.map((tf) => (
                <Badge key={tf.id} color="primary">
                  {tf.name}
                </Badge>
              ))}
            </div>
          )
        },
      },
      {
        id: 'joinedGroup',
        header: t('joined_group'),
        meta: { hideOnMobile: true },
        cell: ({ row }) => {
          const createdAt = row.original.membership?.createdAt
          return createdAt ? formatDate(createdAt) : null
        },
      },
      {
        id: 'joinedOrganisation',
        header: t('joined_organisation'),
        meta: { hideOnMobile: true },
        cell: ({ row }) => {
          const date = row.original.node.externalCreatedAt ?? row.original.node.createdAt
          return date ? formatDate(date) : null
        },
      },
      {
        id: 'lastContacted',
        header: t('last_contacted'),
        meta: { hideOnMobile: true },
        cell: ({ row }) => {
          const lastContactedAt = row.original.node.lastContactedAt
          return lastContactedAt ? formatDate(lastContactedAt) : null
        },
      },
      {
        id: 'engagementLevel',
        header: t('engagement_level'),
        meta: { hideOnMobile: true },
        accessorFn: (row) => row.node.engagementLevel ?? null,
      },
      {
        id: 'contactPreferences',
        header: t('contact_preferences'),
        meta: { hideOnMobile: true, centerContent: true },
        cell: ({ row }) => {
          const { membership } = row.original
          if (!membership) return null

          const preferences = [
            { key: 'emailable' as const, icon: 'mail-01' as const },
            { key: 'textable' as const, icon: 'message-chat-square' as const },
            { key: 'callable' as const, icon: 'phone-call-01' as const },
          ]

          return (
            <div className="tw:flex tw:justify-center tw:gap-2">
              {preferences.map(({ key, icon }) => (
                <Tooltip key={key}>
                  <TooltipTrigger asChild>
                    <span>
                      <Icon
                        type={icon}
                        width={16}
                        height={16}
                        className={membership[key] ? 'tw:text-success-600' : 'tw:text-gray-300'}
                      />
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>{membership[key] ? t(key) : t(`not_${key}`)}</TooltipContent>
                </Tooltip>
              ))}
            </div>
          )
        },
      },
      {
        id: 'federalDistrict',
        header: t('federal_district'),
        meta: { hideOnMobile: true },
        accessorFn: (row) => row.node.federalDistrict ?? null,
      },
      {
        id: 'actions',
        header: t('actions'),
        enableHiding: false,
        enableSorting: false,
        size: 200,
        meta: { stickyRight: true, alignRight: true },
        cell: ({ row }) => {
          const { id, firstName, lastName } = row.original.node
          const { membership } = row.original

          return (
            <SupporterTableActions
              supporterId={id}
              firstName={firstName}
              lastName={lastName}
              groupId={groupId}
              membership={membership}
              canSendIndividualSms={canSendIndividualSms}
              canMakePhoneCalls={canMakePhoneCalls}
              demoSite={demoSite}
            />
          )
        },
      },
    ],
    [groupId, canSendIndividualSms, canMakePhoneCalls, demoSite]
  )

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    manualSorting: true,
    enableHiding: true,
    state: {
      columnVisibility,
    },
    onColumnVisibilityChange,
  })

  return table
}
